import { HttpClient } from '../../../common/infrastructure/http/HttpClient';
import { DeadLetterDetail } from '../../domain/models/DeadLetterDetail';
import { DeadLetterRepository } from '../../domain/ports/DeadLetterRepository';
import { DeadLetterMessage } from '../../domain/models/DeadLetterMessage';

interface DeadLetterMessageDTO {
  id: number;
  error: string;
  body: string;
  queueName: string;
  createdAt: string;
}

interface DeadLetterDetailDTO {
  id: number;
  messageClass: string;
  error: string;
  errorClass: string;
  transport: string;
  failedAt: string;
  history: string[];
  messageContent: string;
}

interface DeadLetterListResponseDTO {
  data: DeadLetterMessageDTO[];
}

interface DeadLetterDetailResponseDTO {
  data: DeadLetterDetailDTO;
}

export class HttpDeadLetterRepository implements DeadLetterRepository {
  constructor(private httpClient: HttpClient) {}

  async getAll(): Promise<DeadLetterMessage[]> {
    try {
      const response =
        await this.httpClient.get<DeadLetterListResponseDTO>('/messaging/dead-letters');

      return response.data.map((dto) => ({
        id: dto.id,
        error: dto.error,
        body: dto.body,
        queueName: dto.queueName,
        createdAt: dto.createdAt,
      }));
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch dead letter messages: ${error.message}`);
      }
      throw error;
    }
  }

  async show(id: number): Promise<DeadLetterDetail> {
    try {
      const response = await this.httpClient.get<DeadLetterDetailResponseDTO>(
        `/messaging/dead-letters/${id}`,
      );
      const dto = response.data;
      return {
        id: dto.id,
        messageClass: dto.messageClass,
        error: dto.error,
        errorClass: dto.errorClass,
        transport: dto.transport,
        failedAt: dto.failedAt,
        history: dto.history,
        messageContent: dto.messageContent,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch dead letter message details: ${error.message}`);
      }
      throw error;
    }
  }

  async retry(id: number): Promise<void> {
    try {
      await this.httpClient.post<void>(`/messaging/dead-letters/${id}/retry`);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to retry dead letter message: ${error.message}`);
      }
      throw error;
    }
  }

  async remove(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/messaging/dead-letters/${id}`);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to remove dead letter message: ${error.message}`);
      }
      throw error;
    }
  }
}
